import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import { storeSession, getUser, clearSession, clearAllSessions, getActiveRole } from '../utils/sessionKeys';
import analytics from '../analytics/manager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error('useAuth must be used within an AuthProvider');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize: check if we have a valid session by calling /profile.
  // If the httpOnly cookie is valid, the backend returns the user profile.
  useEffect(() => {
    const role = getActiveRole();
    const storedUser = getUser(role);

    if (storedUser) {
      // We have stored user metadata — validate by calling profile
      authService.getProfile()
        .then((response) => {
          const profileUser = response.data?.user || response.data || storedUser;
          setUser(profileUser);
          setIsAuthenticated(true);
        })
        .catch(() => {
          // Cookie expired or invalid — clear metadata
          clearSession(role);
          setUser(null);
          setIsAuthenticated(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Listen for cross-tab storage changes — detect logout from another tab
    const handleStorageChange = (e) => {
      const currentRole = getActiveRole();
      const userKey = `user:${currentRole}`;
      if ((e.key === userKey || e.key === 'user') && !e.newValue) {
        setUser(null);
        setIsAuthenticated(false);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async ({ email, password }) => {
    setLoading(true);
    setError('');

    try {
      const response = await authService.login({ email, password });
      const { user } = response.data;
      // Tokens are in httpOnly cookies set by the backend response.
      storeSession(user.role || 'user', user);
      setUser(user);
      setIsAuthenticated(true);
      analytics.identify(String(user.id), { role: user.role });
      analytics.track('user_logged_in', { role: user.role });
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.register(userData);
      const { user } = response.data;
      storeSession(user.role || 'user', user);
      setUser(user);
      setIsAuthenticated(true);
      analytics.identify(String(user.id), { role: user.role });
      analytics.track('user_registered', { role: user.role });
    } catch (err) {
      console.error('Registration error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (credential) => {
    setLoading(true);
    setError('');

    try {
      const response = await authService.googleLogin(credential);
      const { user } = response.data;
      storeSession(user.role || 'user', user);
      setUser(user);
      setIsAuthenticated(true);
      analytics.identify(String(user.id), { role: user.role });
      analytics.track('user_logged_in', { role: user.role, method: 'google' });
    } catch (err) {
      console.error('Google login error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Google login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear user-specific API caches on logout (keep precache + static for offline fallback)
  const clearUserCaches = () => {
    const userDataCaches = [
      'api-kundli-cache',
      'api-horoscope-cache',
      'api-panchanga-cache',
      'api-consultation-cache',
    ];
    if ('caches' in window) {
      caches.keys().then((names) => {
        names
          .filter((name) => userDataCaches.includes(name))
          .forEach((name) => caches.delete(name));
      });
    }
  };

  const logout = () => {
    analytics.track('user_logged_out', { role: user?.role });
    // Call backend to clear httpOnly cookies
    authService.logout().catch(() => {});
    clearAllSessions();
    // Clear user-specific API caches (keep precache + static assets for offline fallback)
    clearUserCaches();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Helper method to manually set user (for external login flows like Login.js/Register.js)
  const setAuthUser = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    storeSession(userData.role || getActiveRole(), userData);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    loginWithGoogle,
    register,
    logout,
    setAuthUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
